import { useCallback, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTimerStore, type TimerState } from '@/shared/stores/timerStore'
import { timerService } from '@/shared/services/timerService'
import { useUIStore } from '@/shared/stores/uiStore'
import { getWebSocketService } from '@/shared/services/websocketService'
import { useWebSocketStore } from '@/shared/stores/websocketStore'
import { offlineQueueService } from '@/shared/services/offlineQueueService'
import { offlineDatabase } from '@/shared/services/offlineDatabase'
import { useOnlineStatus } from '@/shared/hooks/useOnlineStatus'

let globalIntervalRef: ReturnType<typeof setInterval> | null = null
let globalIntervalCount = 0

export interface UseTimerReturn {
  status: TimerState['status']
  elapsedSeconds: number
  startedAt: string | null
  projectId: string | null
  taskId: string | null
  description: string
  isMinimized: boolean
  canStart: boolean
  isOffline: boolean
  start: (projectId: string, taskId: string | null, description: string) => Promise<void>
  pause: () => Promise<void>
  resume: () => Promise<void>
  stop: () => Promise<void>
  cancel: () => Promise<void>
  setDescription: (description: string) => void
  setProjectTask: (projectId: string, taskId: string | null) => void
  toggleMinimize: () => void
  updateStartedAt: (startedAt: string) => Promise<void>
}

export function useTimer(): UseTimerReturn {
  const store = useTimerStore()
  const queryClient = useQueryClient()
  const { addNotification } = useUIStore()
  const { addRecentTimeEntry } = useWebSocketStore()
  const { isOnline } = useOnlineStatus()
  const wsUnsubscribeRef = useRef<(() => void)[]>([])
  
  // Tick timer every second when running (singleton interval shared across all hook instances)
  useEffect(() => {
    if (store.status === 'running') {
      globalIntervalCount++
      if (!globalIntervalRef) {
        globalIntervalRef = setInterval(() => {
          useTimerStore.getState().incrementElapsed()
        }, 1000)
      }
    }
    
    return () => {
      if (store.status === 'running') {
        globalIntervalCount--
        if (globalIntervalCount <= 0 && globalIntervalRef) {
          clearInterval(globalIntervalRef)
          globalIntervalRef = null
          globalIntervalCount = 0
        }
      }
    }
  }, [store.status])
  
  // Subscribe to WebSocket timer events
  useEffect(() => {
    const service = getWebSocketService()
    if (!service) return
    
    // Handle remote timer events from other users (for team visibility)
    const unsubStarted = service.on('timer:started', (data: unknown) => {
      const timerData = data as {
        userId: string
        userName: string
        projectId: string
        taskId: string | null
        description: string
        startedAt: number
      }
      // Add to active timers list for team visibility
      useWebSocketStore.getState().addActiveTimer({
        ...timerData,
        elapsedSeconds: 0,
      })
    })
    
    const unsubPaused = service.on('timer:paused', (data: unknown) => {
      const { userId } = data as { userId: string }
      useWebSocketStore.getState().removeActiveTimer(userId)
    })
    
    const unsubStopped = service.on('timer:stopped', (data: unknown) => {
      const { userId, timeEntry } = data as { 
        userId: string
        timeEntry: {
          id: string
          projectId: string
          taskId: string | null
          description: string
          duration: number
          createdAt: number
        }
      }
      useWebSocketStore.getState().removeActiveTimer(userId)
      // Add to recent time entries
      addRecentTimeEntry({
        ...timeEntry,
        userId,
        userName: 'Team Member', // Will be resolved from user store
      })
    })

    const unsubCancelled = service.on('timer:cancelled', (data: unknown) => {
      const { userId } = data as { userId: string }
      useWebSocketStore.getState().removeActiveTimer(userId)
    })
    
    wsUnsubscribeRef.current = [unsubStarted, unsubPaused, unsubStopped, unsubCancelled]
    
    return () => {
      wsUnsubscribeRef.current.forEach(unsub => unsub())
      wsUnsubscribeRef.current = []
    }
  }, [addRecentTimeEntry])
  
  const start = useCallback(async (projectId: string, taskId: string | null, description: string) => {
    if (!projectId) {
      addNotification({
        type: 'error',
        message: 'Please select a project to start the timer',
        duration: 3000,
      })
      return
    }
    
    try {
      if (isOnline) {
        // Online: sync with server
        await timerService.startTimer({ projectId, taskId: taskId || undefined, description })
        
        // Notify via WebSocket
        const service = getWebSocketService()
        service?.send('timer:started', { projectId, taskId, description, startedAt: Date.now() })
      } else {
        // Offline: queue the action
        await offlineQueueService.queueTimerStart(projectId, taskId, description)
        await offlineDatabase.saveTimer({
          id: `local-${Date.now()}`,
          projectId,
          taskId: taskId || undefined,
          description,
          status: 'running',
          startedAt: Date.now(),
          totalPausedSeconds: 0,
          elapsedSeconds: 0,
        })
        
        addNotification({
          type: 'info',
          message: 'Timer started offline. Will sync when online.',
          duration: 3000,
        })
      }
      
      store.start(projectId, taskId, description)
      
      addNotification({
        type: 'success',
        message: 'Timer started',
        duration: 2000,
      })
    } catch (error) {
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosErr = error as { response?: { status: number } }
        if (axiosErr.response?.status === 409) {
          addNotification({
            type: 'error',
            message: 'A timer is already running. Stop it before starting a new one.',
            duration: 5000,
          })
          // Sync the active timer from the server so the user can stop it
          try {
            const activeTimer = await timerService.getActiveTimer()
            if (activeTimer && activeTimer.startedAt) {
              store.restoreFromStorage({
                status: activeTimer.isRunning ? 'running' : 'paused',
                projectId: activeTimer.projectId ?? null,
                taskId: activeTimer.taskId ?? null,
                description: activeTimer.description ?? '',
                tagIds: [],
                startedAt: activeTimer.startedAt,
                pausedAt: null,
                elapsedSeconds: activeTimer.elapsedSeconds ?? 0,
                isMinimized: false,
              })
            }
          } catch {
            // Silent fallback
          }
          return
        }
      }

      const message = error instanceof Error && error.message === 'No workspace selected'
        ? 'Please select a workspace before starting the timer'
        : 'Failed to start timer. Please try again.'
      addNotification({
        type: 'error',
        message,
        duration: 3000,
      })
    }
  }, [store, addNotification, isOnline])
  
  const pause = useCallback(async () => {
    try {
      if (isOnline) {
        await timerService.pauseTimer()
        
        // Notify via WebSocket
        const service = getWebSocketService()
        service?.send('timer:paused', { pausedAt: Date.now() })
      } else {
        await offlineQueueService.queueTimerPause()
        
        addNotification({
          type: 'info',
          message: 'Timer paused offline. Will sync when online.',
          duration: 2000,
        })
      }
      
      store.pause()
    } catch (error) {
      const message = error instanceof Error && error.message === 'No workspace selected'
        ? 'Please select a workspace first'
        : 'Failed to pause timer'
      addNotification({
        type: 'error',
        message,
        duration: 3000,
      })
    }
  }, [store, addNotification, isOnline])
  
  const resume = useCallback(async () => {
    try {
      if (isOnline) {
        await timerService.resumeTimer()
        
        // Notify via WebSocket
        const service = getWebSocketService()
        service?.send('timer:resumed', { resumedAt: Date.now() })
      } else {
        await offlineQueueService.queueTimerResume()
        
        addNotification({
          type: 'info',
          message: 'Timer resumed offline. Will sync when online.',
          duration: 2000,
        })
      }
      
      store.resume()
    } catch (error) {
      const message = error instanceof Error && error.message === 'No workspace selected'
        ? 'Please select a workspace first'
        : 'Failed to resume timer'
      addNotification({
        type: 'error',
        message,
        duration: 3000,
      })
    }
  }, [store, addNotification, isOnline])
  
  const stop = useCallback(async () => {
    try {
      if (isOnline) {
        const result = await timerService.stopTimer()
        
        // Notify via WebSocket
        const service = getWebSocketService()
        service?.send('timer:stopped', { 
          stoppedAt: Date.now(),
          duration: result.duration,
          timeEntry: result
        })
        
        // Invalidate recent entries and time entries queries to show the new entry
        queryClient.invalidateQueries({ queryKey: ['recent-entries'] })
        queryClient.invalidateQueries({ queryKey: ['timeEntries'] })
        queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] })
        
        addNotification({
          type: 'success',
          message: `Timer stopped. Logged ${Math.floor(result.duration / 60)} minutes.`,
          duration: 3000,
        })
      } else {
        await offlineQueueService.queueTimerStop()
        
        // Create local time entry from timer
        const duration = store.elapsedSeconds
        await offlineDatabase.saveTimeEntry({
          description: store.description,
          projectId: store.projectId!,
          taskId: store.taskId || undefined,
          startTime: new Date(Date.now() - duration * 1000).toISOString(),
          endTime: new Date().toISOString(),
          durationSeconds: duration,
          isBillable: true,
          status: 'draft',
        })
        
        addNotification({
          type: 'success',
          message: `Timer stopped offline. Logged ${Math.floor(duration / 60)} minutes. Will sync when online.`,
          duration: 3000,
        })
      }
      
      store.reset()
    } catch (error) {
      const message = error instanceof Error && error.message === 'No workspace selected'
        ? 'Please select a workspace first'
        : 'Failed to stop timer'
      addNotification({
        type: 'error',
        message,
        duration: 3000,
      })
    }
  }, [store, addNotification, isOnline, queryClient])

  const cancel = useCallback(async () => {
    try {
      if (isOnline) {
        await timerService.cancelTimer()

        const service = getWebSocketService()
        service?.send('timer:cancelled', { cancelledAt: Date.now() })

        addNotification({
          type: 'info',
          message: 'Timer cancelled',
          duration: 2000,
        })
      } else {
        await offlineQueueService.queueTimerCancel()

        addNotification({
          type: 'info',
          message: 'Timer cancelled offline. Will sync when online.',
          duration: 2000,
        })
      }

      store.cancel()
    } catch (error) {
      const message = error instanceof Error && error.message === 'No workspace selected'
        ? 'Please select a workspace first'
        : 'Failed to cancel timer'
      addNotification({
        type: 'error',
        message,
        duration: 3000,
      })
    }
  }, [store, addNotification, isOnline])
  
  const canStart = Boolean(store.projectId)

  const updateStartedAt = useCallback(async (newStartedAt: string) => {
    if (!isOnline) return

    try {
      await timerService.updateTimer({ startTime: newStartedAt })
      store.setStartedAt(newStartedAt)

      addNotification({
        type: 'success',
        message: 'Start time updated',
        duration: 2000,
      })
    } catch {
      addNotification({
        type: 'error',
        message: 'Failed to update start time',
        duration: 3000,
      })
    }
  }, [store, addNotification, isOnline])
  
  return {
    status: store.status,
    elapsedSeconds: store.elapsedSeconds,
    startedAt: store.startedAt,
    projectId: store.projectId,
    taskId: store.taskId,
    description: store.description,
    isMinimized: store.isMinimized,
    canStart,
    isOffline: !isOnline,
    start,
    pause,
    resume,
    stop,
    cancel,
    setDescription: store.setDescription,
    setProjectTask: store.setProjectTask,
    toggleMinimize: store.toggleMinimize,
    updateStartedAt,
  }
}

export default useTimer
